import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { get, post, put, del } from '../../../api/client';
import { copyText } from '../../../lib/clipboard';

const { Text } = Typography;

interface PromotionLink {
  id: string;
  promoterId: string;
  linkCode: string;
  discountType: string;
  discountValue: number;
  rewardType: string | null;
  rewardValue: number | null;
  maxUses: number;
  usedCount: number;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
  /** 服务端拼好的分享地址（基址取自 FRONTEND_URL），不是前端现拼 */
  shareUrl: string;
}

const DISCOUNT_TYPE_OPTIONS = [
  { value: 'percentage', label: '百分比折扣（%）' },
  { value: 'fixed', label: '固定金额减免（¥）' },
  { value: 'month_free', label: '免费月数' },
];

const REWARD_TYPE_OPTIONS = [
  { value: 'month_extension', label: '会员延长（月）' },
  { value: 'percentage', label: '提成百分比（%）' },
];

function discountLabel(r: PromotionLink) {
  if (r.discountType === 'percentage') return `${r.discountValue}% 折扣`;
  if (r.discountType === 'fixed') return `减 ¥${r.discountValue}`;
  return `免 ${r.discountValue} 个月`;
}

function rewardLabel(r: PromotionLink) {
  if (!r.rewardType || r.rewardValue === null) return '—';
  if (r.rewardType === 'month_extension') return `延长 ${r.rewardValue} 个月`;
  return `${r.rewardValue}% 提成`;
}

/**
 * 推广链接（短码 `?promo=`）管理。
 *
 * 与「邀请减免」是两套独立机制：推广链接由管理员为指定推广人配置，优惠与奖励都挂在短码上；
 * 邀请减免由用户自己的邀请码（`?ref=`）承担，规则在「邀请规则」一栏。
 * GET/POST /admin/promotion-links、PUT/DELETE /admin/promotion-links/:id。
 */
export function PromotionLinksTab() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<PromotionLink | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const { data, isLoading } = useQuery({
    queryKey: ['promotion-links'],
    queryFn: () => get<PromotionLink[]>('/admin/promotion-links'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['promotion-links'] });

  const save = useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Record<string, unknown> }) =>
      id ? put(`/admin/promotion-links/${id}`, values) : post('/admin/promotion-links', values),
    onSuccess: () => {
      message.success('推广链接已保存');
      setEditing(null);
      setCreating(false);
      void invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '保存失败'),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      put(`/admin/promotion-links/${id}`, { isActive }),
    onSuccess: () => {
      message.success('状态已更新');
      void invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '操作失败'),
  });

  const openEdit = (link: PromotionLink) => {
    setEditing(link);
    form.setFieldsValue({
      ...link,
      expiresAt: link.expiresAt ? dayjs(link.expiresAt) : null,
    });
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const payload: Record<string, unknown> = {
        discountType: values.discountType,
        discountValue: values.discountValue,
        rewardType: values.rewardType ?? null,
        rewardValue: values.rewardValue ?? null,
        maxUses: values.maxUses ?? 0,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
      };
      if (editing) {
        payload.isActive = values.isActive ?? true;
      } else {
        payload.promoterId = (values.promoterId as string).trim();
      }
      save.mutate({ id: editing?.id, values: payload });
    } catch {
      /* 校验失败 */
    }
  };

  const copyLink = async (row: PromotionLink) => {
    if (!row.shareUrl) {
      message.error('后端未下发分享地址，请确认服务端已部署带分享地址的版本');
      return;
    }
    try {
      await copyText(row.shareUrl);
      message.success('推广链接已复制');
    } catch {
      message.error('浏览器不允许脚本写入剪贴板，请手动选中地址栏复制');
    }
  };

  return (
    <Card
      title="推广链接"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setCreating(true); form.resetFields(); }}>
          新建推广链接
        </Button>
      }
    >
      <Table<PromotionLink>
        rowKey="id"
        size="middle"
        loading={isLoading}
        dataSource={data ?? []}
        pagination={false}
        scroll={{ x: 1170 }}
        columns={[
          {
            title: '短码',
            dataIndex: 'linkCode',
            width: 140,
            render: (v: string, r) => (
              <Space size={4}>
                <Tag color="cyan">{v}</Tag>
                <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => { void copyLink(r); }} title="复制分享地址（不是短码）" />
              </Space>
            ),
          },
          {
            title: '推广人 ID',
            dataIndex: 'promoterId',
            width: 280,
            render: (v: string) => <Text copyable={{ text: v }} style={{ fontSize: 12 }}>{v.slice(0, 8)}…</Text>,
          },
          { title: '优惠', key: 'discount', width: 130, render: (_, r) => discountLabel(r) },
          { title: '推广奖励', key: 'reward', width: 140, render: (_, r) => rewardLabel(r) },
          {
            title: '使用 / 上限',
            key: 'usage',
            width: 110,
            render: (_, r) => `${r.usedCount} / ${r.maxUses > 0 ? r.maxUses : '∞'}`,
          },
          {
            title: '有效期',
            dataIndex: 'expiresAt',
            width: 120,
            render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD') : '长期'),
          },
          {
            title: '状态',
            dataIndex: 'isActive',
            width: 90,
            render: (v: boolean, r) => (
              <Switch
                size="small"
                checked={v}
                loading={toggleActive.isPending && toggleActive.variables?.id === r.id}
                onChange={(checked) => toggleActive.mutate({ id: r.id, isActive: checked })}
              />
            ),
          },
          {
            title: '操作',
            key: 'action',
            width: 160,
            fixed: 'right',
            render: (_, r) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
                  编辑
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    modal.confirm({
                      title: '删除推广链接',
                      content: `确认删除短码「${r.linkCode}」？删除后使用该链接的用户将无法享受优惠。`,
                      okButtonProps: { danger: true },
                      onOk: async () => {
                        await del(`/admin/promotion-links/${r.id}`);
                        message.success('删除成功');
                        void invalidate();
                      },
                    })
                  }
                >
                  删除
                </Button>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editing ? '编辑推广链接' : '新建推广链接'}
        open={creating || editing !== null}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={submit}
        confirmLoading={save.isPending}
      >
        <Form form={form} layout="vertical">
          {!editing && (
            <Form.Item
              name="promoterId"
              label="推广人 ID（Party ID）"
              rules={[{ required: true, message: '请输入推广人的 Party ID' }]}
            >
              <Input placeholder="填写推广人（客户）的 Party ID" />
            </Form.Item>
          )}
          <Form.Item name="discountType" label="优惠类型" rules={[{ required: true }]}>
            <Select options={DISCOUNT_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="discountValue" label="优惠值" rules={[{ required: true, message: '请输入优惠值' }]}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="rewardType" label="推广人奖励类型（可选）">
            <Select options={REWARD_TYPE_OPTIONS} allowClear placeholder="不设置奖励" />
          </Form.Item>
          <Form.Item name="rewardValue" label="奖励值">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="maxUses" label="使用上限（0 = 不限）" initialValue={0}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="expiresAt" label="有效期至">
            <DatePicker style={{ width: '100%' }} showTime />
          </Form.Item>
          {editing && (
            <Form.Item name="isActive" label="是否启用" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
}
